import { useEffect, useState, useCallback, useRef } from 'react'
import { useScopeStore } from '../store'
import { getGroupById, getFieldById } from '../schema'
import { productManagersApi, presalesDashboardApi } from '../../../api/client'
import { api } from '../../../api/client'
import type { JiraTicketStatusResult } from '../../../api/client'
import type { PmApprovalStatus } from '../types'
import { showToast } from '@store/uiStore'

export function CustomWorkTab() {
  const { scope, updateGeneralInfo } = useScopeStore()
  const { generalInfo } = scope
  const disabled = scope.finalized

  // Fetch product managers for assignment dropdown
  const [productManagers, setProductManagers] = useState<Array<{ id: number; name: string; is_active: boolean }>>([])
  const [pmLoading, setPmLoading] = useState(true)
  const [creatingTasks, setCreatingTasks] = useState<Record<number, boolean>>({})

  // Linked custom work task statuses (keyed by custom_work_id)
  const [cwTaskStatuses, setCwTaskStatuses] = useState<Record<number, Array<{ id: number; status: string; description: string }>>>({})

  // Live Jira ticket statuses
  const [liveJiraStatuses, setLiveJiraStatuses] = useState<Record<string, JiraTicketStatusResult>>({})
  const [jiraStatusLoading, setJiraStatusLoading] = useState(false)
  const lastFetchedTicketsRef = useRef<string>('')

  const fetchJiraStatuses = useCallback(async () => {
    // Collect all non-empty Jira ticket strings from custom works
    const allTickets: string[] = []
    for (const cw of generalInfo.customWorks) {
      if (cw.jiraTickets) {
        for (const jt of cw.jiraTickets) {
          if (jt.ticket.trim()) allTickets.push(jt.ticket.trim())
        }
      }
    }

    if (allTickets.length === 0) {
      setLiveJiraStatuses({})
      lastFetchedTicketsRef.current = ''
      return
    }

    // Avoid re-fetching if tickets haven't changed
    const ticketKey = allTickets.sort().join(',')
    if (ticketKey === lastFetchedTicketsRef.current) return

    setJiraStatusLoading(true)
    try {
      const res = await api.jira.fetchPresalesTicketStatuses(allTickets)
      if (res.success && res.data?.statuses) {
        setLiveJiraStatuses(res.data.statuses)
        lastFetchedTicketsRef.current = ticketKey
      }
    } catch (err) {
      console.error('Failed to fetch Jira ticket statuses:', err)
    } finally {
      setJiraStatusLoading(false)
    }
  }, [generalInfo.customWorks])

  // Fetch live statuses on mount and when custom works change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJiraStatuses()
    }, 500)
    return () => clearTimeout(timer)
  }, [fetchJiraStatuses])

  useEffect(() => {
    productManagersApi.list().then((response) => {
      if (response.success && response.data) {
        setProductManagers(response.data.items)
      }
      setPmLoading(false)
    }).catch(() => {
      setPmLoading(false)
    })
  }, [])

  // Fetch linked custom work task statuses
  useEffect(() => {
    if (!scope.hubspotDealId) return
    const hasCwItems = generalInfo.customWorks.some((cw) => cw.tasksLinked)
    if (!hasCwItems) return

    presalesDashboardApi.getCustomWorkTasks(scope.hubspotDealId).then((res) => {
      if (res.success && res.data) {
        const grouped: Record<number, Array<{ id: number; status: string; description: string }>> = {}
        for (const task of res.data) {
          const cwId = (task as any).custom_work_id as number
          if (!cwId) continue
          if (!grouped[cwId]) grouped[cwId] = []
          grouped[cwId].push({ id: task.id, status: task.status, description: task.description })
        }
        setCwTaskStatuses(grouped)
      }
    }).catch(() => {})
  }, [scope.hubspotDealId, generalInfo.customWorks])

  const customWorksGroup = getGroupById('general_custom_works_group')
  const customWorksField = getFieldById('general_custom_works')

  const categoryLabel = (category: string) => {
    switch (category) {
      case 'report':
        return 'Report'
      case 'portal':
        return 'Portal'
      case 'kyc':
        return 'KYC'
      case 'idv':
        return 'IDV'
      case 'onesdk':
        return 'OneSDK'
      case 'kyb':
        return 'KYB'
      case 'txm':
        return 'TxM'
      case 'fraud':
        return 'Fraud'
      case 'risk':
        return 'Risk'
      case 'api':
        return 'API'
      default:
        return 'Other'
    }
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">
          {customWorksGroup?.formTitle || customWorksField?.formLabel || 'Custom Works'}
        </h3>
      </div>

      <div className="space-y-3">
        <p className="text-xs text-[var(--text-muted)]">
          {customWorksGroup?.formDescription ||
            customWorksField?.formDescription ||
            'Record client-specific changes agreed outside the standard configuration options.'}
        </p>

        {generalInfo.customWorks.length === 0 ? (
          <div className="text-sm text-[var(--text-secondary)]">No custom works added yet.</div>
        ) : (
          <div className="space-y-6">
            {generalInfo.customWorks.map((item) => (
              <div key={item.id} className={`rounded-lg p-4 space-y-4 ${
                item.tasksLinked
                  ? 'bg-[var(--bg-secondary)] border border-[var(--border-color)]'
                  : item.name
                    ? 'bg-amber-50/30 border-2 border-amber-400'
                    : 'bg-[var(--bg-secondary)] border border-[var(--border-color)]'
              }`}>
                {/* Warning: PM not approved */}
                {item.assignedPmId && (item.pmApprovalStatus || 'not_requested') !== 'approved' && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-amber-700 text-xs flex items-center gap-2">
                    <span>⚠️</span>
                    <span>PM has not yet approved this custom work{(item.pmApprovalStatus || 'not_requested') === 'rejected' ? ' (rejected)' : ''}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                  <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Change Name</label>
                    <input
                      type="text"
                      className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      placeholder="Short name"
                      value={item.name}
                      onChange={(e) =>
                        updateGeneralInfo(
                          'customWorks',
                          generalInfo.customWorks.map((x) => (x.id === item.id ? { ...x, name: e.target.value } : x))
                        )
                      }
                      disabled={disabled}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Category</label>
                    <select
                      className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      value={item.category}
                      onChange={(e) =>
                        updateGeneralInfo(
                          'customWorks',
                          generalInfo.customWorks.map((x) => (x.id === item.id ? { ...x, category: e.target.value as any } : x))
                        )
                      }
                      disabled={disabled}
                    >
                      {(
                        [
                          'report',
                          'portal',
                          'kyc',
                          'idv',
                          'onesdk',
                          'kyb',
                          'txm',
                          'fraud',
                          'risk',
                          'api',
                          'other',
                        ] as const
                      ).map((c) => (
                        <option key={c} value={c}>
                          {categoryLabel(c)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Status</label>
                    <select
                      className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      value={item.lifecycleStatus || 'draft'}
                      onChange={(e) =>
                        updateGeneralInfo(
                          'customWorks',
                          generalInfo.customWorks.map((x) =>
                            x.id === item.id ? { ...x, lifecycleStatus: e.target.value as any } : x
                          )
                        )
                      }
                      disabled={disabled}
                    >
                      <option value="draft">Draft</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="on_hold">On hold</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        Assigned Product Manager
                      </label>
                      <select
                        className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={item.assignedPmId ?? ''}
                        onChange={(e) =>
                          updateGeneralInfo(
                            'customWorks',
                            generalInfo.customWorks.map((x) =>
                              x.id === item.id ? { ...x, assignedPmId: e.target.value ? Number(e.target.value) : null } : x
                            )
                          )
                        }
                        disabled={disabled}
                      >
                        <option value="">None</option>
                        {pmLoading ? (
                          <option value="" disabled>Loading...</option>
                        ) : productManagers && productManagers.length > 0 ? (
                          productManagers
                            .filter((pm) => pm.is_active)
                            .map((pm) => (
                              <option key={pm.id} value={pm.id}>
                                {pm.name}
                              </option>
                            ))
                        ) : (
                          <option value="" disabled>No product managers found</option>
                        )}
                      </select>
                    </div>

                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        PM Approval
                      </label>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const status = item.pmApprovalStatus || 'not_requested'
                          const statusConfig: Record<string, { label: string; color: string; bg: string; border: string }> = {
                            not_requested: { label: 'Not Requested', color: 'text-[var(--text-secondary)]', bg: 'bg-[var(--bg-tertiary)]', border: 'border-[var(--border-color)]' },
                            pending: { label: 'Pending', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300' },
                            approved: { label: 'Approved', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-300' },
                            rejected: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-300' },
                          }
                          const cfg = statusConfig[status] || statusConfig.not_requested
                          return (
                            <span className={`inline-flex items-center h-8 px-3 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                              {status === 'approved' && <span className="mr-1">&#10003;</span>}
                              {status === 'rejected' && <span className="mr-1">&#10007;</span>}
                              {status === 'pending' && <span className="mr-1">&#9679;</span>}
                              {cfg.label}
                            </span>
                          )
                        })()}
                        <select
                          className="h-8 px-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          value={item.pmApprovalStatus || 'not_requested'}
                          onChange={(e) =>
                            updateGeneralInfo(
                              'customWorks',
                              generalInfo.customWorks.map((x) =>
                                x.id === item.id ? { ...x, pmApprovalStatus: e.target.value as PmApprovalStatus } : x
                              )
                            )
                          }
                          disabled={disabled}
                        >
                          <option value="not_requested">Not Requested</option>
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        Due Date
                        {item.isHardDate && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 border border-red-300">
                            Hard Date
                          </span>
                        )}
                      </label>
                      <input
                        type="date"
                        className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={item.dueDate || ''}
                        onChange={(e) =>
                          updateGeneralInfo(
                            'customWorks',
                            generalInfo.customWorks.map((x) =>
                              x.id === item.id ? { ...x, dueDate: e.target.value } : x
                            )
                          )
                        }
                        disabled={disabled}
                      />
                      <label className="flex items-center gap-2 mt-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.isHardDate || false}
                          onChange={(e) =>
                            updateGeneralInfo(
                              'customWorks',
                              generalInfo.customWorks.map((x) =>
                                x.id === item.id ? { ...x, isHardDate: e.target.checked } : x
                              )
                            )
                          }
                          disabled={disabled}
                          className="rounded border-[var(--border-color)] text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-[var(--text-primary)]">
                          Hard date <span className="text-xs text-[var(--text-secondary)]">(firm client commitment)</span>
                        </span>
                      </label>
                    </div>

                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Effort (days)</label>
                      <input
                        type="number"
                        min={0}
                        className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        placeholder="e.g. 5"
                        value={item.effortDays ?? ''}
                        onChange={(e) => {
                          const next = e.target.value === '' ? null : Number(e.target.value)
                          updateGeneralInfo(
                            'customWorks',
                            generalInfo.customWorks.map((x) =>
                              x.id === item.id ? { ...x, effortDays: Number.isFinite(next as number) ? next : null } : x
                            )
                          )
                        }}
                        disabled={disabled}
                      />
                    </div>

                    <div className="md:col-span-4 md:flex md:justify-end md:items-end md:pt-7 gap-2">
                    {item.tasksLinked ? (
                      <span className="inline-flex items-center gap-2 h-12 px-3 bg-green-50 border border-green-300 text-green-700 rounded-lg text-sm font-medium">
                        <span>✓ Tasks Created</span>
                        {cwTaskStatuses[item.id] && (() => {
                          const tasks = cwTaskStatuses[item.id]
                          const done = tasks.filter((t) => t.status === 'completed').length
                          return (
                            <span className="text-xs text-green-600">
                              ({done}/{tasks.length} done)
                            </span>
                          )
                        })()}
                      </span>
                    ) : item.name?.trim() ? (
                      <button
                        type="button"
                        className="h-12 px-3 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                        disabled={disabled || creatingTasks[item.id]}
                        onClick={async () => {
                          if (!scope.hubspotDealId) {
                            showToast('This scope is not linked to a HubSpot deal. Please link it first.', 'warning')
                            return
                          }
                          setCreatingTasks((prev) => ({ ...prev, [item.id]: true }))
                          try {
                            const res = await presalesDashboardApi.createCustomWorkTasks({
                              hubspot_deal_id: scope.hubspotDealId,
                              custom_work_id: item.id,
                              custom_work_name: item.name,
                              custom_work_category: item.category,
                              custom_work_description: item.description,
                              due_date: item.dueDate || undefined,
                              jira_ticket: item.jiraTickets?.[0]?.ticket || undefined,
                              assigned_pm_id: item.assignedPmId || undefined,
                            })
                            if (res.success || (res.error && res.error.includes('already exist'))) {
                              updateGeneralInfo(
                                'customWorks',
                                generalInfo.customWorks.map((x) =>
                                  x.id === item.id ? { ...x, tasksLinked: true } : x
                                )
                              )
                            } else {
                              showToast(res.error || 'Failed to create tasks', 'error')
                            }
                          } catch (err) {
                            console.error('Error creating custom work tasks:', err)
                            showToast('Failed to create tasks', 'error')
                          } finally {
                            setCreatingTasks((prev) => ({ ...prev, [item.id]: false }))
                          }
                        }}
                      >
                        {creatingTasks[item.id] ? 'Creating...' : 'Add as Tasks'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="h-12 px-3 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
                      onClick={() =>
                        updateGeneralInfo(
                          'customWorks',
                          generalInfo.customWorks.filter((x) => x.id !== item.id)
                        )
                      }
                      disabled={disabled}
                    >
                      Remove
                    </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Description</label>
                  <textarea
                    className="w-full min-h-24 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
                    placeholder="Describe what's changing and why"
                    value={item.description}
                    onChange={(e) =>
                      updateGeneralInfo(
                        'customWorks',
                        generalInfo.customWorks.map((x) => (x.id === item.id ? { ...x, description: e.target.value } : x))
                      )
                    }
                    disabled={disabled}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)]">Jira Tickets</label>
                      <p className="text-xs text-[var(--text-secondary)] mt-1">Add one or more Jira tickets linked to this change. Live status is fetched from Jira.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.jiraTickets && item.jiraTickets.some(jt => jt.ticket.trim()) && (
                        <button
                          type="button"
                          className="px-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-secondary)] rounded-lg text-xs font-medium hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50"
                          onClick={() => {
                            lastFetchedTicketsRef.current = ''
                            fetchJiraStatuses()
                          }}
                          disabled={jiraStatusLoading}
                        >
                          {jiraStatusLoading ? '↻ Refreshing...' : '↻ Refresh Status'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                        onClick={() =>
                          updateGeneralInfo(
                            'customWorks',
                            generalInfo.customWorks.map((x) =>
                              x.id === item.id
                                ? {
                                    ...x,
                                    jiraTickets: [...(x.jiraTickets || []), { id: Date.now(), ticket: '', status: 'tbd' }],
                                  }
                                : x
                            )
                          )
                        }
                        disabled={disabled}
                      >
                        Add Ticket
                      </button>
                    </div>
                  </div>

                  {(!item.jiraTickets || item.jiraTickets.length === 0) ? (
                    <div className="text-sm text-[var(--text-secondary)]">No tickets added.</div>
                  ) : (
                    <div className="space-y-3">
                      {item.jiraTickets.map((t) => {
                        // Find live status for this ticket
                        const ticketKey = t.ticket.trim().toUpperCase()
                        // Try to find by exact key or by extracting the key from a URL
                        const liveStatus = Object.values(liveJiraStatuses).find(
                          (s) => s.key === ticketKey || t.ticket.includes(s.key)
                        )

                        const statusCategoryColor = (cat: string) => {
                          switch (cat) {
                            case 'new':
                              return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' }
                            case 'indeterminate':
                              return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' }
                            case 'done':
                              return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' }
                            default:
                              return { bg: 'bg-[var(--bg-tertiary)]', text: 'text-[var(--text-secondary)]', border: 'border-[var(--border-color)]' }
                          }
                        }

                        return (
                          <div key={t.id} className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                            {/* Ticket header with key badge, summary, status, and actions */}
                            {liveStatus ? (
                              <div className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-tertiary)]">
                                <a
                                  href={t.ticket.startsWith('http') ? t.ticket : `https://frankieone.atlassian.net/browse/${liveStatus.key}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm font-mono font-semibold hover:bg-blue-100 transition-colors shrink-0"
                                >
                                  {liveStatus.key}
                                  <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                </a>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-[var(--text-primary)] truncate" title={liveStatus.summary}>
                                    {liveStatus.summary}
                                  </p>
                                </div>
                                <div className={`inline-flex items-center h-7 px-2.5 rounded-full text-xs font-medium shrink-0 ${statusCategoryColor(liveStatus.statusCategory).bg} ${statusCategoryColor(liveStatus.statusCategory).text} border ${statusCategoryColor(liveStatus.statusCategory).border}`}>
                                  {liveStatus.statusCategory === 'done' && <span className="mr-1">✓</span>}
                                  {liveStatus.statusCategory === 'indeterminate' && <span className="mr-1">●</span>}
                                  {liveStatus.status}
                                </div>
                                <button
                                  type="button"
                                  className="h-7 px-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50 shrink-0"
                                  onClick={() =>
                                    updateGeneralInfo(
                                      'customWorks',
                                      generalInfo.customWorks.map((x) =>
                                        x.id === item.id
                                          ? { ...x, jiraTickets: (x.jiraTickets || []).filter((y) => y.id !== t.id) }
                                          : x
                                      )
                                    )
                                  }
                                  disabled={disabled}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              /* No live status — show editable input row */
                              <div className="flex flex-col md:flex-row gap-3 items-start p-4">
                                <div className="flex-1 min-w-0">
                                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Ticket</label>
                                  <input
                                    type="text"
                                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                    placeholder="e.g. PROJ-123 or https://..."
                                    value={t.ticket}
                                    onChange={(e) =>
                                      updateGeneralInfo(
                                        'customWorks',
                                        generalInfo.customWorks.map((x) =>
                                          x.id === item.id
                                            ? {
                                                ...x,
                                                jiraTickets: (x.jiraTickets || []).map((y) =>
                                                  y.id === t.id ? { ...y, ticket: e.target.value } : y
                                                ),
                                              }
                                            : x
                                        )
                                      )
                                    }
                                    disabled={disabled}
                                  />
                                </div>

                                <div className="w-full md:w-32 shrink-0">
                                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                                    Status
                                    {jiraStatusLoading && t.ticket.trim() && (
                                      <span className="ml-1 text-xs text-[var(--text-muted)] font-normal">...</span>
                                    )}
                                  </label>
                                  {t.ticket.trim() ? (
                                    <div className="h-10 flex items-center text-xs text-[var(--text-muted)]">
                                      {jiraStatusLoading ? 'Fetching...' : 'No status found'}
                                    </div>
                                  ) : (
                                    <div className="h-10 flex items-center text-xs text-[var(--text-muted)]">
                                      Enter a ticket key
                                    </div>
                                  )}
                                </div>

                                <div className="w-full md:w-36 shrink-0">
                                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Manual Override</label>
                                  <select
                                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-2 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                    value={t.status}
                                    onChange={(e) =>
                                      updateGeneralInfo(
                                        'customWorks',
                                        generalInfo.customWorks.map((x) =>
                                          x.id === item.id
                                            ? {
                                                ...x,
                                                jiraTickets: (x.jiraTickets || []).map((y) =>
                                                  y.id === t.id ? { ...y, status: e.target.value as any } : y
                                                ),
                                              }
                                            : x
                                        )
                                      )
                                    }
                                    disabled={disabled}
                                  >
                                    <option value="tbd">TBD</option>
                                    <option value="todo">To do</option>
                                    <option value="in_progress">In progress</option>
                                    <option value="blocked">Blocked</option>
                                    <option value="done">Done</option>
                                  </select>
                                </div>

                                <div className="shrink-0 md:pt-7">
                                  <button
                                    type="button"
                                    className="h-10 px-3 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50 whitespace-nowrap"
                                    onClick={() =>
                                      updateGeneralInfo(
                                        'customWorks',
                                        generalInfo.customWorks.map((x) =>
                                          x.id === item.id
                                            ? { ...x, jiraTickets: (x.jiraTickets || []).filter((y) => y.id !== t.id) }
                                            : x
                                        )
                                      )
                                    }
                                    disabled={disabled}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <button
            type="button"
            className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            onClick={() =>
              updateGeneralInfo('customWorks', [
                ...generalInfo.customWorks,
                {
                  id: Date.now(),
                  name: '',
                  description: '',
                  category: 'other',
                  dueDate: '',
                  effortDays: null,
                  lifecycleStatus: 'draft',
                  jiraTickets: [],
                  assignedPmId: null,
                  pmApprovalStatus: 'not_requested',
                  tasksLinked: false,
                },
              ])
            }
            disabled={disabled}
          >
            Add Custom Work
          </button>
        </div>
      </div>
    </div>
  )
}
